/**
 * Zone tariffs of the object form: the client's zone rates become the
 * 24-cell wall-clock profile of `PUT /sites/:id/tariff`, which the plan of a
 * zone tariff reads (`services/schedule-builder.ts#zoneHourPrices`).
 * Zones as in the prototype: night 23:00–07:00; 3-zone peak 08:00–11:00 and
 * 20:00–22:00. A fixed tariff has one rate («Ставка»): 24 equal cells, read by
 * «Гроші» only — a fixed tariff has no plan.
 */
import type { TariffType } from 'shared';

export type ZoneTariff = Extract<TariffType, 'zone2' | 'zone3'>;
/** A tariff with rates typed by the client: the zones or the fixed rate. */
export type RatedTariff = ZoneTariff | 'fixed';
export type ZoneName = 'night' | 'day' | 'peak' | 'half' | 'flat';

export const ZONES: Record<RatedTariff, ZoneName[]> = {
  zone2: ['night', 'day'],
  zone3: ['night', 'peak', 'half'],
  fixed: ['flat'],
};

/** Prototype rates, ₴/kWh — the starting values of an empty form. */
export const DEFAULT_RATES: Record<RatedTariff, Partial<Record<ZoneName, number>>> = {
  zone2: { night: 3.25, day: 6.5 },
  zone3: { night: 2.6, peak: 9.75, half: 6.5 },
  fixed: { flat: 4.32 },
};

export function isZone(t: TariffType | null | undefined): t is ZoneTariff {
  return t === 'zone2' || t === 'zone3';
}

export function hasRates(t: TariffType | null | undefined): t is RatedTariff {
  return isZone(t) || t === 'fixed';
}

export function zoneOfHour(tariff: RatedTariff, h: number): ZoneName {
  if (tariff === 'fixed') return 'flat';
  if (h >= 23 || h < 7) return 'night';
  if (tariff === 'zone2') return 'day';
  return (h >= 8 && h < 11) || (h >= 20 && h < 22) ? 'peak' : 'half';
}

/** 24 wall-clock cells of the zone rates. */
export function zoneCells(tariff: RatedTariff, rates: Partial<Record<ZoneName, number>>): number[] {
  return Array.from({ length: 24 }, (_, h) => rates[zoneOfHour(tariff, h)] ?? 0);
}

/** Zone rates read back from 24 cells, if they follow the zones of `tariff`. */
export function ratesFromCells(
  tariff: RatedTariff,
  cells: readonly number[],
): Partial<Record<ZoneName, number>> | null {
  if (cells.length !== 24) return null;
  const out: Partial<Record<ZoneName, number>> = {};
  for (let h = 0; h < 24; h += 1) {
    const z = zoneOfHour(tariff, h);
    const v = cells[h] ?? 0;
    if (out[z] === undefined) out[z] = v;
    else if (Math.abs((out[z] ?? 0) - v) > 1e-9) return null;
  }
  return out;
}
